import { Injectable, signal } from '@angular/core';
import { GeneralService } from './general.service';
import { HttpClient } from '@angular/common/http';
import { baseUrl } from 'src/environments/environment';
import { setErrorContext } from './error-reporter';

@Injectable({
  providedIn: 'root'
})
export class StateService {
  private readonly sideMenuCollapsedStorageKey = 'ALTOMATE_SIDE_MENU_COLLAPSED';

  constructor(
    private generalService: GeneralService,
    private http: HttpClient
  ) {

    const screenWidth = window.innerWidth;
    this._open_sidemenu = signal<boolean>(this.getInitialSideMenuCollapsed(screenWidth)); // Signal for navbar state
  }

  private _selected_company = signal<any | null>({});
  private _user = signal<any | null>({});
  private _open_sidemenu;
  private _open_notificationsidemenu = signal<boolean>(false); // Signal for navbar state
  private _tasks = signal<any | null>([]);
  private _resolutions = signal<any | null>([]);
  private _company_details = signal<any | null>({});
  private _notifications = signal<any | null>([]);
  private _company_list = signal<any | null>([]);
  private _country_settings = signal<any | null>({});
  private _is_nric_completed = signal<boolean>(false); // Signal for navbar state
  private _kyc_completed = signal<boolean>(false); // Signal for navbar state
  private _selfie_completed = signal<boolean>(false); // Signal for navbar state
  private _signature_completed = signal<boolean>(false); // Signal for navbar state
  private _trigger_getMe = signal<number>(null); // Signal for navbar state
  private _show_company_list = signal<boolean>(false); // Signal for navbar state
  private _company_invitation_list = signal<any | null>([]);
  private _self_invitation_list = signal<any | null>([]);
  private _invitation_details = signal<any | null>({});
  private _update_percentage = signal<number | null>(0);
  private _websiteMetaData = signal<any | null>({});
  private _google_pay_status = signal<boolean>(null); // Signal for navbar state
  private _apple_pay_status = signal<boolean>(null); // Signal for navbar state
  private _pending_instant_resolution_key = signal<string | null>(null);
  private _pending_instant_resolution_started_at = signal<number | null>(null);
  private _pending_replacement_seed = signal<any | null>(null);


  get pendingInstantResolutionKey() {
    return this._pending_instant_resolution_key();
  }

  // Epoch ms of when the pending instant resolution was submitted. Used to drive
  // the non-blocking "preparing your document" status card + staged messaging.
  get pendingInstantResolutionStartedAt() {
    return this._pending_instant_resolution_started_at();
  }

  setPendingInstantResolutionKey(key: string | null) {
    this._pending_instant_resolution_key.set(key);
    this._pending_instant_resolution_started_at.set(key ? Date.now() : null);
  }

  // Carries data when the user switches from resign-director / appoint-director
  // into the combined director-replacement flow, so they don't re-enter what they had.
  get pendingReplacementSeed() {
    return this._pending_replacement_seed();
  }

  setPendingReplacementSeed(seed: any | null) {
    this._pending_replacement_seed.set(seed);
  }

  consumePendingReplacementSeed(): any | null {
    const seed = this._pending_replacement_seed();
    this._pending_replacement_seed.set(null);
    return seed;
  }


  get isGooglePayAvailable() {
    return this._google_pay_status();
  }

  updateGooglePayStatus(data) {
    this._google_pay_status.set(data)
  }


  get isApplePayAvailable() {
    return this._apple_pay_status();
  }

  updateApplePayStatus(data) {
    this._apple_pay_status.set(data)
  }

  get websiteMetaData() {
    return this._websiteMetaData();
  }

  setWebsiteMetaData(data) {
    this._websiteMetaData.set(data)
  }


  get updatePercentage() {
    return this._update_percentage();
  }

  setUpdatePercentage(percentage) {
    this._update_percentage.set(percentage)
  }

  get invitationDetails() {
    return this._invitation_details();
  }

  updateInvitationDetails(info) {
    this._invitation_details.set(info)
  }


  get userInfo() {
    return this._user();
  }

  setUserInfo(userinfo: any) {
    this._user.set(userinfo)
    // Ids only, so an error report says who hit it without carrying personal data.
    setErrorContext({ userId: userinfo?.id ?? userinfo?.uid ?? userinfo?.user_id });
  }

  get openSideMenu() {
    return this._open_sidemenu();
  }

  toggleSideMenu() {
    const nextValue = !this.openSideMenu;
    this._open_sidemenu.set(nextValue)
    this.persistSideMenuCollapsed(nextValue);
  }

  private getInitialSideMenuCollapsed(screenWidth: number): boolean {
    if (screenWidth < 768) return false;

    const storedValue = localStorage.getItem(this.sideMenuCollapsedStorageKey);
    if (storedValue === 'true') return true;
    if (storedValue === 'false') return false;

    return true;
  }

  private persistSideMenuCollapsed(collapsed: boolean): void {
    if (window.innerWidth < 768) return;
    localStorage.setItem(this.sideMenuCollapsedStorageKey, collapsed.toString());
  }

  get openNotificationSideMenu() {
    return this._open_notificationsidemenu();
  }

  toggleNotificationSideMenu() {
    this._open_notificationsidemenu.set(!this.openNotificationSideMenu)
  }

  get tasksList() {
    return this._tasks();
  }

  setTaskList(data: any) {
    this._tasks.set(data)
  }

  removeTask(id) {
    const updatedTasks = this._tasks().filter(task => task.task_id !== id);
    this._tasks.set(updatedTasks);

    console.log(this._company_details())
    this.http.get(`${baseUrl}/partial-tasks/${this._company_details()['id']}`).subscribe((result) => {
      this.setTaskList((result['tasks_list'] || []))
    })
  }


  removeRequest(id) {
    const updatedRequests = this._resolutions().filter(request => request.deal_id !== id);
    this._resolutions.set(updatedRequests);
  }

  get resolutionList() {
    return this._resolutions();
  }

  setResolutionList(data: any) {
    this._resolutions.set(data)
  }

  get selectedCompany() {
    return this._selected_company();
  }

  updateSelectedCompany(data: any) {
    data['display_name'] = data['display_name'] === 'New Business' ? `New Business ${this._company_list().length + 1}` : data['display_name']
    this._selected_company.set(data)
    setErrorContext({ organisationId: data?.id });
  }

  updateSelectedCompanyName(name) {
    let selectedCompany = this._selected_company()
    selectedCompany['display_name'] = name
    this._selected_company.set(selectedCompany)
  }

  get companyDetails() {
    return this._company_details();
  }

  setCompanyDetails(data: any) {
    this._company_details.set(data)
    this.generalService.isApplicationSubmitted = data['submit']
  }

  get companyInvitationList() {
    return this._company_invitation_list();
  }

  setCompanyInvitationList(data: any) {
    this._company_invitation_list.set(data)
  }

  get selfInvitationList() {
    return this._self_invitation_list();
  }

  setSelfInvitationList(data: any) {
    this._self_invitation_list.set(data)
  }

  // this.stateService.setInvitations(result['data'] || [])
  //         this.generalService.loadingInvitations = false

  get isKYCCompleted() {
    return this._kyc_completed();
  }

  setKYCComplete(status = true) {
    this._kyc_completed.set(status)
  }

  get isNRICCompleted() {
    return this._is_nric_completed();
  }

  setNRICCompleted(status = true) {
    this._is_nric_completed.set(status)
  }

  get isSelfieCompleted() {
    return this._selfie_completed();
  }

  setSelfieComplete(status = true) {
    this._selfie_completed.set(status)
  }

  get isSignatureCompleted() {
    return this._signature_completed();
  }

  setSignatureCompleted(status = true) {
    this._signature_completed.set(status)
  }

  get showCompanyList() {
    return this._show_company_list();
  }

  setCompanyList(value = !this._show_company_list()) {
    this._show_company_list.set(value)
  }

  get getMe() {
    return this._trigger_getMe();
  }

  setGetMe() {
    this._trigger_getMe.set(Date.now())
  }

  get notificationList() {
    return this._notifications();
  }

  setNotifications(data: any) {
    this._notifications.set(data)
  }

  readNotifications(id) {
    const updatedNotifications = this._notifications()
    let index = updatedNotifications.findIndex(g => g['id'] === id)
    updatedNotifications[index]['is_read'] = 1
    this._notifications.set(updatedNotifications)
  }

  addNotifications(data: any) {
    const updatedNotifications = this._notifications().concat(data);
    this._notifications.set(updatedNotifications)
  }

  get companyList() {
    return this._company_list();
  }

  updateCompanyList(data: any) {
    this._company_list.set(data)
    this.checkCompanyListExist()
  }

  addCompanyList(data: any) {
    data['display_name'] = `New Business ${this._company_list().length + 1}`
    let tempList = JSON.parse(JSON.stringify(this._company_list()))
    const updatedCompanyList = tempList.concat([data]);
    this._company_list.set(updatedCompanyList)
    this.checkCompanyListExist()
  }

  removeCompanyFromList(id) {
    let updatedCompanyList = this._company_list().filter(g => g['id'] !== id)
    updatedCompanyList = updatedCompanyList.map(((g: object, index: number) => ({ ...g, display_name: ((g['display_name'] || '').includes('New Business') ? `New Business ${index + 1}` : g['display_name']) })))
    this._company_list.set(updatedCompanyList)
    this.checkCompanyListExist()
  }

  checkCompanyListExist() {
    if (this._company_list().length === 0) {
      this.generalService.isCompanylistExists = false
    } else {
      this.generalService.isCompanylistExists = true
    }
  }

  get countrySetting() {
    return this._country_settings();
  }

  updateCountrySettings(data: any) {
    this._country_settings.set(data)
  }





}
